import { useMemo, useState } from "react";
import ListboxSelect from "components/ui/Select";
import { useSearchParams } from "react-router-dom";
import { MdFilterList, MdClose } from "react-icons/md";

export const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Newest first" },
  { value: "createdAt:asc", label: "Oldest first" },
  { value: "closesAt:asc", label: "Closing soonest" },
  { value: "closesAt:desc", label: "Closing latest" },
  { value: "opensAt:asc", label: "Opening soonest" },
  { value: "opensAt:desc", label: "Opening latest" },
  { value: "updatedAt:desc", label: "Recently changed" },
  { value: "question:asc", label: "Question A to Z" },
  { value: "question:desc", label: "Question Z to A" },
  { value: "status:asc", label: "Status" },
];

export const ORIGIN_LABEL = { MANUAL: "Created by hand", IMPORT: "Imported from a file", FEED: "From a source feed", AUTOMATION: "Automation" };
const VISIBILITY_OPTIONS = ["PUBLIC", "UNLISTED", "INTERNAL"];
const SOURCE_TYPE_LABEL = { MANUAL: "Manual", API_FEED: "API feed", SCHEDULED_EVENT: "Scheduled event" };

const FILTER_KEYS = [
  "statuses",
  "visibility",
  "origin",
  "sourceType",
  "sourceProvider",
  "createdBy",
  "uploadId",
  "clusterId",
  "partnerId",
  "tenantId",
  "createdFrom",
  "createdTo",
  "opensFrom",
  "opensTo",
  "closesFrom",
  "closesTo",
];

const DATE_RANGES = [
  { label: "Created", from: "createdFrom", to: "createdTo" },
  { label: "Opens", from: "opensFrom", to: "opensTo" },
  { label: "Closes", from: "closesFrom", to: "closesTo" },
];

function dayBoundary(value, end) {
  if (!value) return undefined;
  const [y, m, d] = value.split("-").map(Number);
  const date = end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function useMarketFilters({ legacyStatusKey = "status" } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const out = {};
    for (const key of FILTER_KEYS) {
      const value = searchParams.get(key);
      if (value) out[key] = value;
    }
    const legacy = searchParams.get(legacyStatusKey);
    if (legacy && !out.statuses) out.statuses = legacy;
    return out;
  }, [searchParams, legacyStatusKey]);

  const sortValue = searchParams.get("sort") || "createdAt:desc";
  const [sort, dir] = sortValue.split(":");

  function update(patch) {
    const next = new URLSearchParams(searchParams);
    if ("statuses" in patch) next.delete(legacyStatusKey);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  function clearAll() {
    const next = new URLSearchParams(searchParams);
    for (const key of FILTER_KEYS) next.delete(key);
    next.delete(legacyStatusKey);
    setSearchParams(next, { replace: true });
  }

  const apiParams = useMemo(() => {
    const params = { sort: sort || "createdAt", dir: dir === "asc" ? "asc" : "desc" };
    for (const [key, value] of Object.entries(filters)) {
      if (key.endsWith("From")) params[key] = dayBoundary(value, false);
      else if (key.endsWith("To")) params[key] = dayBoundary(value, true);
      else params[key] = value;
    }
    return params;
  }, [filters, sort, dir]);

  return {
    filters,
    apiParams,
    sort: { sort: sort || "createdAt", dir: dir === "asc" ? "asc" : "desc" },
    setSort: (nextSort, nextDir) => update({ sort: `${nextSort}:${nextDir}` }),
    setFilter: (key, value) => update({ [key]: value }),
    clearAll,
    activeCount: Object.keys(filters).length,
  };
}

const selectClass =
  "h-9 w-full rounded-md border border-border bg-transparent px-3 text-sm text-foreground outline-none transition-[color,box-shadow] focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30";
const labelClass = "mb-1 block text-xs font-medium text-muted-foreground";

function Select({ label, value, onChange, options, placeholder = "Any" }) {
  return (
    <div className="block">
      <span className={labelClass}>{label}</span>
      <ListboxSelect
        ariaLabel={label}
        value={value || ""}
        onChange={(v) => onChange(v || undefined)}
        options={[{ value: "", label: placeholder }, ...options.map((o) => ({ value: o.value, label: o.label }))]}
      />
    </div>
  );
}

export default function MarketFilters({ state, statuses, statusLabel = (s) => s, options = {}, fields = {} }) {
  const [open, setOpen] = useState(state.activeCount > 0);
  const { filters, setFilter } = state;
  const selectedStatuses = filters.statuses ? filters.statuses.split(",") : [];
  const creators = options.creators || [];
  const providers = options.providers || [];
  const uploads = options.uploads || [];
  const clusters = options.clusters || [];
  const partners = options.partners || [];
  const tenants = options.tenants || [];

  function toggleStatus(status) {
    const next = selectedStatuses.includes(status) ? selectedStatuses.filter((s) => s !== status) : [...selectedStatuses, status];
    setFilter("statuses", next.join(","));
  }

  const chips = [];
  if (selectedStatuses.length) chips.push({ key: "statuses", label: `Status: ${selectedStatuses.map(statusLabel).join(", ")}` });
  if (filters.visibility) chips.push({ key: "visibility", label: `Visibility: ${filters.visibility}` });
  if (filters.origin) chips.push({ key: "origin", label: `How: ${ORIGIN_LABEL[filters.origin] || filters.origin}` });
  if (filters.sourceType) chips.push({ key: "sourceType", label: `Source: ${SOURCE_TYPE_LABEL[filters.sourceType] || filters.sourceType}` });
  if (filters.sourceProvider) chips.push({ key: "sourceProvider", label: `Provider: ${filters.sourceProvider}` });
  if (filters.createdBy) chips.push({ key: "createdBy", label: `Created by: ${creators.find((c) => c.id === filters.createdBy)?.name || "selected person"}` });
  if (filters.uploadId) chips.push({ key: "uploadId", label: `File: ${uploads.find((u) => u.id === filters.uploadId)?.fileName || "selected upload"}` });
  if (filters.clusterId) chips.push({ key: "clusterId", label: `Cluster: ${clusters.find((c) => String(c.id) === String(filters.clusterId))?.label || filters.clusterId}` });
  if (filters.partnerId) chips.push({ key: "partnerId", label: `Partner: ${partners.find((p) => p.id === filters.partnerId)?.name || "selected partner"}` });
  if (filters.tenantId) chips.push({ key: "tenantId", label: `Tenant: ${tenants.find((t) => t.id === filters.tenantId)?.name || "selected tenant"}` });
  for (const range of DATE_RANGES) {
    if (filters[range.from]) chips.push({ key: range.from, label: `${range.label} from ${filters[range.from]}` });
    if (filters[range.to]) chips.push({ key: range.to, label: `${range.label} until ${filters[range.to]}` });
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Sort</span>
          <ListboxSelect
            ariaLabel="Sort markets"
            className="min-w-[10.5rem]"
            value={`${state.sort.sort}:${state.sort.dir}`}
            onChange={(v) => {
              const [s, d] = String(v).split(":");
              state.setSort(s, d);
            }}
            options={[
              ...SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
              ...(!SORT_OPTIONS.some((o) => o.value === `${state.sort.sort}:${state.sort.dir}`)
                ? [{ value: `${state.sort.sort}:${state.sort.dir}`, label: "Custom" }]
                : []),
            ]}
          />
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={`flex h-9 items-center gap-1.5 rounded-sm border px-3 text-sm font-medium transition-colors ${
            state.activeCount > 0
              ? "border-primary bg-primary/10 text-primary"
              : "border-border bg-background text-foreground hover:bg-accent dark:bg-input/30 dark:hover:bg-input/50"
          }`}
        >
          <MdFilterList /> Filters{state.activeCount > 0 ? ` (${state.activeCount})` : ""}
        </button>
        {chips.map((chip) => (
          <span key={chip.key} className="flex items-center gap-1 rounded-md border border-transparent bg-secondary py-0.5 pl-2 pr-1 text-xs font-medium text-secondary-foreground">
            {chip.label}
            <button type="button" aria-label={`Remove ${chip.label}`} onClick={() => setFilter(chip.key, undefined)} className="rounded-sm p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground">
              <MdClose />
            </button>
          </span>
        ))}
        {state.activeCount > 0 && (
          <button type="button" onClick={state.clearAll} className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
            Clear all
          </button>
        )}
      </div>

      {open && (
        <div className="mt-3 rounded-xl border border-border bg-muted/40 p-4">
          <span className={labelClass}>Status</span>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {statuses.map((status) => {
              const on = selectedStatuses.includes(status);
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleStatus(status)}
                  className={`h-8 rounded-md px-3 text-sm font-medium transition-colors ${
                    on ? "bg-primary text-primary-foreground" : "border border-border bg-background text-foreground hover:bg-accent dark:bg-input/30"
                  }`}
                >
                  {statusLabel(status)}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {fields.tenant && tenants.length > 0 && (
              <Select label="Tenant" value={filters.tenantId} onChange={(v) => setFilter("tenantId", v)} options={tenants.map((t) => ({ value: t.id, label: t.name }))} />
            )}
            {fields.partner && partners.length > 0 && (
              <Select label="Partner" value={filters.partnerId} onChange={(v) => setFilter("partnerId", v)} options={partners.map((p) => ({ value: p.id, label: p.name }))} />
            )}
            <Select
              label="Created by"
              value={filters.createdBy}
              onChange={(v) => setFilter("createdBy", v)}
              options={creators.map((c) => ({ value: c.id, label: `${c.name} (${c.count})` }))}
            />
            <Select
              label="How it was created"
              value={filters.origin}
              onChange={(v) => setFilter("origin", v)}
              options={Object.entries(ORIGIN_LABEL).map(([value, label]) => ({ value, label }))}
            />
            {uploads.length > 0 && (
              <Select label="Uploaded file" value={filters.uploadId} onChange={(v) => setFilter("uploadId", v)} options={uploads.map((u) => ({ value: u.id, label: `${u.fileName} (${u.count})` }))} />
            )}
            <Select label="Visibility" value={filters.visibility} onChange={(v) => setFilter("visibility", v)} options={VISIBILITY_OPTIONS.map((v) => ({ value: v, label: v }))} />
            <Select
              label="Source type"
              value={filters.sourceType}
              onChange={(v) => setFilter("sourceType", v)}
              options={Object.entries(SOURCE_TYPE_LABEL).map(([value, label]) => ({ value, label }))}
            />
            {providers.length > 0 && (
              <Select
                label="Provider"
                value={filters.sourceProvider}
                onChange={(v) => setFilter("sourceProvider", v)}
                options={providers.map((p) => ({ value: p.value, label: `${p.value} (${p.count})` }))}
              />
            )}
            {clusters.length > 0 && (
              <Select label="Cluster" value={filters.clusterId} onChange={(v) => setFilter("clusterId", v)} options={clusters.map((c) => ({ value: String(c.id), label: c.label }))} />
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {DATE_RANGES.map((range) => (
              <div key={range.label}>
                <span className={labelClass}>{range.label}</span>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    aria-label={`${range.label} from`}
                    className={selectClass}
                    value={filters[range.from] || ""}
                    onChange={(e) => setFilter(range.from, e.target.value)}
                  />
                  <span className="text-xs text-gray-400">to</span>
                  <input
                    type="date"
                    aria-label={`${range.label} until`}
                    className={selectClass}
                    value={filters[range.to] || ""}
                    onChange={(e) => setFilter(range.to, e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
